import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Pause, Play, Loader2, GripVertical } from "lucide-react";
import { useAudio } from "../context/AudioContext";
import { useTranslate } from "../context/TranslateContext";

// Always-available floating audio control. Appears on every page whenever TTS audio is
// active, and can be dragged out of the way. One tap on ✕ shuts audio off instantly.
export const GlobalAudioBar = () => {
  const audio = useAudio();
  const ctx = useTranslate();
  const t = ctx ? ctx.t : (x) => x;

  const active = audio && (audio.state === "playing" || audio.state === "paused" || audio.state === "loading");

  return (
    <AnimatePresence>
      {active && (
        <motion.div
          data-testid="global-audio-bar"
          drag
          dragMomentum={false}
          dragElastic={0.06}
          initial={{ opacity: 0, y: 24, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 24, scale: 0.96 }}
          transition={{ type: "spring", stiffness: 380, damping: 30 }}
          className="fixed bottom-5 right-5 z-[70] flex items-center gap-3 rounded-full border border-gold/40 bg-paper/95 py-2 pl-2 pr-2.5 shadow-[0_10px_40px_-8px_rgba(0,0,0,0.45)] backdrop-blur-xl"
        >
          {/* Drag handle */}
          <span data-testid="audio-drag-handle" className="flex cursor-grab touch-none items-center text-ash active:cursor-grabbing" title={t("Drag to move")}>
            <GripVertical className="h-4 w-4" />
          </span>

          {/* Play / Pause */}
          <button
            data-testid="audio-toggle"
            onClick={() => (audio.state === "playing" ? audio.pause() : audio.resume())}
            disabled={audio.state === "loading"}
            aria-label={audio.state === "playing" ? t("Pause") : t("Play")}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-gold text-ink transition-transform hover:scale-105 disabled:opacity-60"
          >
            {audio.state === "loading" ? <Loader2 className="h-4 w-4 animate-spin" />
              : audio.state === "playing" ? <Pause className="h-4 w-4" />
              : <Play className="h-4 w-4" />}
          </button>

          {/* Label + progress */}
          <div className="min-w-0 max-w-[42vw] sm:max-w-[220px]">
            <div className="flex items-center gap-2">
              <span className="truncate font-body text-sm text-cream">{t(audio.source?.label || "Listening")}</span>
              {audio.part?.n > 1 && (
                <span className="shrink-0 font-body text-[0.65rem] text-ash">{audio.part.i + 1}/{audio.part.n}</span>
              )}
            </div>
            <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-edge">
              <div className="h-full bg-gold transition-[width] duration-150" style={{ width: `${(audio.progress || 0) * 100}%` }} />
            </div>
          </div>

          {/* Speed */}
          <button
            data-testid="audio-speed"
            onClick={audio.cycleSpeed}
            className="shrink-0 rounded-full border border-edge px-2.5 py-1 font-body text-xs text-ash transition-colors hover:text-gold"
          >
            {audio.speed}x
          </button>

          {/* Stop / close */}
          <button
            data-testid="audio-stop"
            onClick={audio.stop}
            aria-label={t("Stop audio")}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-edge text-ash transition-colors hover:border-red-400 hover:bg-red-500/10 hover:text-red-400"
          >
            <X className="h-4 w-4" />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
